type ErrorResponse = { data: { detail: string }; status: number };

export function isErrorWithMessage(error: unknown): error is ErrorResponse {
  return (
    typeof error === "object" &&
    error != null &&
    "data" in error &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (error as any).data.detail === "string"
  );
}

/**
 * Extracts a user-friendly error message from API errors.
 * Handles common Django REST framework error formats:
 * - non_field_errors: ["error message"]
 * - detail: "error message"
 * - message: "error message"
 * - field_name: ["error message"]
 */
export function getErrorMessage(
  error: unknown,
  fallbackMessage = "An unexpected error occurred. Please try again."
): string {
  const e = error as { data?: Record<string, unknown> | string[] };

  if (e?.data) {
    // Handle data as a direct array of error messages
    if (Array.isArray(e.data)) {
      return e.data.join(", ");
    }

    // Handle non_field_errors array (common validation error format)
    if (Array.isArray(e.data.non_field_errors)) {
      return e.data.non_field_errors.join(", ");
    }

    // Handle detail message (Django REST framework standard)
    if (typeof e.data.detail === "string") {
      return e.data.detail;
    }

    // Handle message field
    if (typeof e.data.message === "string") {
      return e.data.message;
    }

    // Handle field-specific errors
    const fieldErrors = Object.entries(e.data)
      .filter(
        ([key]) => !["non_field_errors", "detail", "message"].includes(key)
      )
      .map(([key, value]) => {
        const message = Array.isArray(value) ? value.join(", ") : String(value);
        // Convert snake_case to readable format
        const readableKey = key.replace(/_/g, " ");
        return `${readableKey}: ${message}`;
      });

    if (fieldErrors.length > 0) {
      return fieldErrors.join("; ");
    }
  }

  return fallbackMessage;
}
